import { useState } from 'react';
import { Avatar, Button, Flex, Input, Modal, Typography } from 'antd';
import { ChatCircle } from '@phosphor-icons/react';
import type { WmsShipmentInboundType } from 'schemas/shipment/inbound/detail';
import { COLOR } from '@styles/theme';
import { DateUtility, DateFormat } from '@utility/date';
import { ShipmentsService, ToastMessage } from '@utility/index';

const { Text } = Typography;

type InboundShipmentCommentsTabProps = {
  data: WmsShipmentInboundType;
  onRefresh: () => void;
};

function formatDateTime(iso?: string) {
  if (!iso) return '—';
  return DateUtility.formatDate(iso, DateFormat.dateTime);
}

export function InboundShipmentCommentsTab({
  data,
  onRefresh,
}: InboundShipmentCommentsTabProps) {
  const comments = data.comments ?? [];
  const [modalOpen, setModalOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);

  const handleAdd = async () => {
    const comment = draft.trim();
    if (!comment || !data._id) return;

    try {
      setPosting(true);
      await ShipmentsService.addComment(data._id, { comment });
      ToastMessage.success('Comment added');
      setDraft('');
      setModalOpen(false);
      onRefresh();
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setPosting(false);
    }
  };

  return (
    <Flex vertical gap={16} style={{ padding: '8px 0 0' }}>
      {comments.length === 0 ? (
        <Text type="secondary" style={{ fontSize: 14 }}>
          No comments yet.
        </Text>
      ) : (
        <Flex vertical gap={16}>
          {comments.map(comment => (
            <Flex key={comment._id} gap={12} align="flex-start">
              <Avatar size={32} style={{ background: COLOR.black, flexShrink: 0 }}>
                {(comment.authorName || comment.author || '?').charAt(0)}
              </Avatar>
              <Flex vertical gap={4} style={{ minWidth: 0 }}>
                <Flex gap={8} wrap="wrap">
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    {comment.authorName || comment.author}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    {formatDateTime(comment.createdAt)}
                  </Text>
                </Flex>
                <Text style={{ fontSize: 14 }}>{comment.text}</Text>
              </Flex>
            </Flex>
          ))}
        </Flex>
      )}

      <Button
        block
        icon={<ChatCircle size={16} />}
        onClick={() => setModalOpen(true)}
        style={{ marginTop: 8 }}
      >
        Add Comment
      </Button>

      <Modal
        title="Add Comment"
        open={modalOpen}
        onCancel={() => {
          if (posting) return;
          setModalOpen(false);
          setDraft('');
        }}
        onOk={handleAdd}
        okText="Post"
        confirmLoading={posting}
        destroyOnHidden
      >
        <Input.TextArea
          rows={4}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          placeholder="Write a comment…"
          disabled={posting}
        />
      </Modal>
    </Flex>
  );
}
